import { reactive } from 'vue'

import FlagSwapService from '@/services/flag-swap.service'
import { errorMessage, infoMessage } from '@/utils/notifications'

/**
 * Shared state and requests for the temporary swap modal and the active swaps
 * banner. The environments screen and the environment picker both open the
 * same modal through this.
 *
 * @returns {{
 *   modal: Object,
 *   active: Object,
 *   open: (targetKey?: string|null) => void,
 *   close: () => void,
 *   preview: (projectKey: string, request: Object) => Promise<void>,
 *   confirm: (projectKey: string, request: Object) => Promise<void>,
 *   loadActive: (projectKey: string, environmentKeys: string[]) => Promise<void>,
 *   revert: (projectKey: string, targetEnvironmentKey: string, overlay: Object) => Promise<void>,
 *   history: Object,
 *   openHistory: (targetKey?: string|null) => void,
 *   closeHistory: () => void,
 *   loadHistory: (projectKey: string, options?: { append?: boolean }) => Promise<void>,
 *   selectHistoryEnvironment: (projectKey: string, targetKey: string) => Promise<void>,
 * }}
 */
export function useSwapFlow() {
    // Swaps still applying on the listed environments, and the ones being reverted right now.
    const active = reactive({
        overlays: [],
        loading: false,
        revertingUlids: [],
    })

    const modal = reactive({
        show: false,
        processing: false,
        diff: null,
        diffLoading: false,
        // Target preselected when the modal opens from a specific environment.
        initialTargetKey: null,
    })

    // Incremented per preview so a slow earlier response can't overwrite a newer one.
    let previewRequest = 0

    // Audit history of the swaps applied to one target environment, with its filters and paging (ZEN-767).
    const history = reactive({
        show: false,
        targetKey: null,
        events: [],
        actors: [],
        loading: false,
        loadingMore: false,
        hasMore: false,
        nextCursor: null,
        filters: { actorUlid: '', createdFrom: '', createdTo: '' },
    })

    // Incremented per history load so a slow earlier page can't overwrite the results of a newer filter.
    let historyRequest = 0

    const open = (targetKey = null) => {
        modal.initialTargetKey = targetKey
        modal.show = true
    }

    const close = () => {
        modal.show = false
        modal.diff = null
        modal.diffLoading = false
        previewRequest += 1
    }

    const preview = async (projectKey, { sourceUlid, targetKey }) => {
        const requestId = ++previewRequest
        modal.diff = null
        modal.diffLoading = true
        try {
            const data = await FlagSwapService.preview(
                projectKey,
                targetKey,
                sourceUlid
            )
            if (requestId === previewRequest) {
                modal.diff = data.data
            }
        } catch (error) {
            errorMessage(error)
        } finally {
            if (requestId === previewRequest) {
                modal.diffLoading = false
            }
        }
    }

    const confirm = async (projectKey, { targetEnvironmentKey, payload }) => {
        modal.processing = true
        try {
            await FlagSwapService.create(
                projectKey,
                targetEnvironmentKey,
                payload
            )
            infoMessage('Swap created. It reverts automatically when it ends.')
            close()
        } catch (error) {
            errorMessage(error)
        } finally {
            modal.processing = false
        }
    }

    const loadActive = async (projectKey, environmentKeys) => {
        active.loading = true
        try {
            const responses = await Promise.all(
                environmentKeys.map((key) =>
                    FlagSwapService.list(projectKey, key)
                )
            )
            active.overlays = responses.flatMap((response) =>
                Array.isArray(response?.data) ? response.data : []
            )
        } catch (error) {
            errorMessage(error)
        } finally {
            active.loading = false
        }
    }

    const revert = async (projectKey, targetEnvironmentKey, overlay) => {
        active.revertingUlids.push(overlay.ulid)
        try {
            await FlagSwapService.revert(
                projectKey,
                targetEnvironmentKey,
                overlay.ulid
            )
            infoMessage(
                'Swap reverted. The target is serving its published values again.'
            )
            removeActive(overlay.ulid)
        } catch (error) {
            // 409 means someone else already reverted it, so it no longer belongs in the banner.
            if (error?.response?.status === 409) {
                removeActive(overlay.ulid)
            }
            errorMessage(error)
        } finally {
            active.revertingUlids = active.revertingUlids.filter(
                (ulid) => ulid !== overlay.ulid
            )
        }
    }

    const removeActive = (overlayUlid) => {
        active.overlays = active.overlays.filter(
            (overlay) => overlay.ulid !== overlayUlid
        )
    }

    const openHistory = (targetKey = null) => {
        history.targetKey = targetKey
        history.filters.actorUlid = ''
        history.filters.createdFrom = ''
        history.filters.createdTo = ''
        history.show = true
    }

    const closeHistory = () => {
        history.show = false
        historyRequest += 1
        history.loading = false
        history.loadingMore = false
    }

    const historyParams = (cursor) => {
        const { actorUlid, createdFrom, createdTo } = history.filters

        return {
            actor_ulid: actorUlid || undefined,
            created_from: createdFrom || undefined,
            // A chosen date covers the whole day, so the range runs to its last second.
            created_to: createdTo ? `${createdTo}T23:59:59Z` : undefined,
            cursor: cursor || undefined,
        }
    }

    /**
     * Load the first page of history for the selected target, or the next page when appending. Filters are read from
     * history.filters, so the caller sets them before reloading.
     */
    const loadHistory = async (projectKey, { append = false } = {}) => {
        if (history.targetKey === null) {
            return
        }

        const requestId = ++historyRequest
        if (append) {
            history.loadingMore = true
        } else {
            history.loading = true
            history.events = []
            history.hasMore = false
            history.nextCursor = null
        }

        try {
            const body = await FlagSwapService.history(
                projectKey,
                history.targetKey,
                historyParams(append ? history.nextCursor : null)
            )
            if (requestId !== historyRequest) {
                return
            }

            history.events = append
                ? [...history.events, ...body.data]
                : body.data
            history.actors = body.actors
            history.hasMore = body.meta.has_more
            history.nextCursor = body.meta.next_cursor
        } catch (error) {
            if (requestId === historyRequest) {
                errorMessage(error)
            }
        } finally {
            if (requestId === historyRequest) {
                history.loading = false
                history.loadingMore = false
            }
        }
    }

    const selectHistoryEnvironment = (projectKey, targetKey) => {
        history.targetKey = targetKey
        return loadHistory(projectKey)
    }

    return {
        modal,
        active,
        open,
        close,
        preview,
        confirm,
        loadActive,
        revert,
        history,
        openHistory,
        closeHistory,
        loadHistory,
        selectHistoryEnvironment,
    }
}
